import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  DISEASE_NOTIFICATION_STATES,
  NOTIFIABLE_DISEASE_CODES,
  OUTBREAK_STATUSES,
  type ContactTraceEntry,
  type DiseaseNotification,
  type EpidemiologyDashboard,
  type Facility,
  type Outbreak,
  type Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, OutbreakStatusBadge, PageHeader, Spinner, StatCard, StatusBadge, Tabs } from '../../ui/components';

const TAB_KEYS = ['dashboard', 'notifications', 'outbreaks'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * Communicable-disease surveillance — packages/modules/m25-public-health/src/routes/{diseaseNotifications,outbreaks}.ts.
 * reportingFacilityId is verified live against M24 eHealth's own facility
 * directory (a real GET /api/v1/m24/facilities/:id check server-side, see
 * src/service/ehealthClient.ts) — the facility picker below calls the same
 * m24 endpoint this portal's eHealth workspace uses, through this app's own
 * gateway, to populate valid choices.
 */
export function Surveillance() {
  const [tab, setTab] = useState<TabKey>('dashboard');
  return (
    <div>
      <PageHeader title="Disease Surveillance" subtitle="Statutory notifications, outbreak dashboard, and dormant-until-outbreak contact tracing." />
      <Tabs
        tabs={[
          { key: 'dashboard', label: 'Dashboard' },
          { key: 'notifications', label: 'Notifications' },
          { key: 'outbreaks', label: 'Outbreaks' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'dashboard' ? <DashboardTab /> : null}
      {tab === 'notifications' ? <NotificationsTab /> : null}
      {tab === 'outbreaks' ? <OutbreaksTab /> : null}
    </div>
  );
}

function DashboardTab() {
  const { persona } = useAuth();
  const canView = (persona?.roles.includes('EPIDEMIOLOGIST') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const dashQ = useApiQuery<EpidemiologyDashboard>(canView ? '/api/v1/m25/epidemiology/dashboard' : null);

  if (!canView) return <ErrorNote message="The epidemiology dashboard requires EPIDEMIOLOGIST or SENIOR_PHI." />;
  if (dashQ.loading) return <Spinner />;
  if (dashQ.error) return <ErrorNote message={dashQ.error} />;
  const d = dashQ.data;
  if (!d) return null;

  const diseaseEntries = Object.entries(d.byDisease).sort(([, a], [, b]) => b - a);
  const districtEntries = Object.entries(d.byDistrict).sort(([, a], [, b]) => b - a);
  const maxDisease = Math.max(1, ...diseaseEntries.map(([, n]) => n));
  const maxDistrict = Math.max(1, ...districtEntries.map(([, n]) => n));

  return (
    <div>
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <StatCard label="Active cases" value={d.totalActiveCases} tone={d.totalActiveCases > 0 ? 'warn' : 'good'} />
        <StatCard label="Active outbreaks" value={d.activeOutbreaks} tone={d.activeOutbreaks > 0 ? 'bad' : 'good'} />
        <StatCard label="Contained outbreaks" value={d.containedOutbreaks} tone="warn" />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Cases by disease</h2>
          {diseaseEntries.length === 0 ? (
            <EmptyState title="No active cases" />
          ) : (
            <ul className="space-y-2">
              {diseaseEntries.map(([code, n]) => (
                <li key={code} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 font-medium text-slate-600">{code.replaceAll('_', ' ')}</span>
                  <div className="h-2.5 flex-1 rounded-full bg-slate-100">
                    <div className="h-2.5 rounded-full bg-brand-500" style={{ width: `${(n / maxDisease) * 100}%` }} />
                  </div>
                  <span className="w-6 text-right font-semibold tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Cases by district</h2>
          {districtEntries.length === 0 ? (
            <EmptyState title="No active cases" />
          ) : (
            <ul className="space-y-2">
              {districtEntries.map(([district, n]) => (
                <li key={district} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 font-medium text-slate-600">{district}</span>
                  <div className="h-2.5 flex-1 rounded-full bg-slate-100">
                    <div className="h-2.5 rounded-full bg-seagreen-500" style={{ width: `${(n / maxDistrict) * 100}%` }} />
                  </div>
                  <span className="w-6 text-right font-semibold tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationsTab() {
  const { persona } = useAuth();
  const canView = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI') || persona?.roles.includes('EPIDEMIOLOGIST')) ?? false;
  const [status, setStatus] = useState('');
  const [diseaseCode, setDiseaseCode] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (diseaseCode) qs.set('diseaseCode', diseaseCode);
  const listQ = useApiQuery<Page<DiseaseNotification>>(`/api/v1/m25/disease-notifications?${qs.toString()}`);

  return (
    <div>
      {!canView ? (
        <div className="mb-4">
          <ErrorNote message="Viewing notifications requires PH_INSPECTOR, SENIOR_PHI or EPIDEMIOLOGIST." />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <select className="field-input !w-52" value={diseaseCode} onChange={(e) => setDiseaseCode(e.target.value)}>
            <option value="">All diseases</option>
            {NOTIFIABLE_DISEASE_CODES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            {DISEASE_NOTIFICATION_STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + File notification
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No notifications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">Disease</th>
                <th className="table-head">Patient (NIN)</th>
                <th className="table-head">Facility district</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((n) => (
                <tr key={n.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(n.id)}>
                  <td className="table-cell font-mono text-xs">{n.refNo}</td>
                  <td className="table-cell font-medium">{n.diseaseCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell">{n.nin}</td>
                  <td className="table-cell">{n.reportingFacilityDistrict}</td>
                  <td className="table-cell">
                    <StatusBadge status={n.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateNotificationModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <NotificationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function CreateNotificationModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  // Real cross-module read: this is m24-ehealth's own facility directory,
  // reached through this gateway's /api/v1/m24 proxy — the same endpoint
  // m25's own backend calls server-side to validate reportingFacilityId.
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [nin, setNin] = useState('');
  const [diseaseCode, setDiseaseCode] = useState<string>(NOTIFIABLE_DISEASE_CODES[0]);
  const [onsetDate, setOnsetDate] = useState('');
  const [reportingFacilityId, setReportingFacilityId] = useState('');
  const [patientDistrict, setPatientDistrict] = useState('');
  const [hospitalized, setHospitalized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/disease-notifications', {
        nin,
        diseaseCode,
        onsetDate: onsetDate || undefined,
        reportingFacilityId,
        patientDistrict: patientDistrict || undefined,
        hospitalized,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file notification');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File a statutory disease notification" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Patient NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Disease">
          <select className="field-input" value={diseaseCode} onChange={(e) => setDiseaseCode(e.target.value)}>
            {NOTIFIABLE_DISEASE_CODES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reporting facility (m24-ehealth)">
          <select className="field-input" value={reportingFacilityId} onChange={(e) => setReportingFacilityId(e.target.value)}>
            <option value="">Select a facility…</option>
            {(facilitiesQ.data?.items ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} — {f.district}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Onset date (optional)">
            <input type="date" className="field-input" value={onsetDate} onChange={(e) => setOnsetDate(e.target.value)} />
          </Field>
          <Field label="Patient district (optional)">
            <input className="field-input" value={patientDistrict} onChange={(e) => setPatientDistrict(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={hospitalized} onChange={(e) => setHospitalized(e.target.checked)} />
          Patient hospitalized
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || nin.trim().length < 5 || !reportingFacilityId}>
            {busy ? 'Filing…' : 'File notification'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NotificationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { persona } = useAuth();
  const isEpidemiologist = persona?.roles.includes('EPIDEMIOLOGIST') ?? false;
  const isEpiOrSenior = (isEpidemiologist || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const detailQ = useApiQuery<DiseaseNotification>(`/api/v1/m25/disease-notifications/${id}`);
  const outbreaksQ = useApiQuery<Page<Outbreak>>(isEpidemiologist ? '/api/v1/m25/outbreaks?limit=100' : null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outbreakId, setOutbreakId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNin, setContactNin] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const n = detailQ.data;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/disease-notifications/${id}${path}`, body);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const addContact = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/disease-notifications/${id}/contacts`, {
        contactName,
        contactNin: contactNin || undefined,
        contactPhone: contactPhone || undefined,
      });
      setContactName('');
      setContactNin('');
      setContactPhone('');
      detailQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add contact');
    } finally {
      setBusy(false);
    }
  };

  const tracingActive = n?.outbreak && (n.outbreak.status === 'ACTIVE' || n.outbreak.status === 'CONTAINED');

  return (
    <Modal title="Disease notification" onClose={onClose} wide>
      {detailQ.loading || !n ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={n.state} />
            {n.hospitalized ? <span className="text-xs font-semibold text-rose-600">Hospitalized</span> : null}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Disease</dt>
              <dd className="font-medium">{n.diseaseCode.replaceAll('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Patient NIN</dt>
              <dd className="font-medium">{n.nin}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Reporting facility</dt>
              <dd className="font-medium">{n.reportingFacilityName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Onset</dt>
              <dd className="font-medium">{n.onsetDate ? formatDate(n.onsetDate) : '—'}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {n.state === 'NOTIFIED' && isEpiOrSenior ? (
              <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => run('/investigate')} disabled={busy}>
                Start investigation
              </button>
            ) : null}
            {n.state === 'UNDER_INVESTIGATION' && isEpidemiologist ? (
              <>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => run('/confirm')} disabled={busy}>
                  Confirm
                </button>
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => run('/rule-out')} disabled={busy}>
                  Rule out
                </button>
              </>
            ) : null}
            {(n.state === 'CONFIRMED' || n.state === 'RULED_OUT') && isEpiOrSenior ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => run('/close')} disabled={busy}>
                Close
              </button>
            ) : null}
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Outbreak link</div>
            {n.outbreak ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="font-medium">{n.outbreak.refNo}</span>
                <OutbreakStatusBadge status={n.outbreak.status} />
              </div>
            ) : n.state !== 'CLOSED' && isEpidemiologist ? (
              <div className="flex gap-2">
                <select className="field-input !w-56" value={outbreakId} onChange={(e) => setOutbreakId(e.target.value)}>
                  <option value="">Select an outbreak…</option>
                  {(outbreaksQ.data?.items ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.refNo} — {o.diseaseCode}
                    </option>
                  ))}
                </select>
                <button className="btn-secondary shrink-0 !px-2.5 !py-1 text-xs" disabled={!outbreakId || busy} onClick={() => run('/link-outbreak', { outbreakId })}>
                  Link
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-400">Not linked to an outbreak.</p>
            )}
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Contact tracing</div>
            {!tracingActive ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Contact tracing is dormant until this case is linked to an outbreak that is ACTIVE or CONTAINED.
              </p>
            ) : (
              <>
                {(n.contacts?.length ?? 0) === 0 ? (
                  <p className="mb-2 text-xs text-slate-400">No contacts logged yet.</p>
                ) : (
                  <ul className="mb-2 space-y-1">
                    {(n.contacts ?? []).map((c: ContactTraceEntry) => (
                      <li key={c.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs">
                        <span>
                          {c.contactName} {c.contactPhone ? `· ${c.contactPhone}` : ''}
                        </span>
                        <StatusBadge status={c.monitoringStatus} />
                      </li>
                    ))}
                  </ul>
                )}
                <div className="grid grid-cols-3 gap-1.5">
                  <input className="field-input" placeholder="Name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
                  <input className="field-input" placeholder="NIN (optional)" value={contactNin} onChange={(e) => setContactNin(e.target.value)} />
                  <input className="field-input" placeholder="Phone (optional)" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                </div>
                <button className="btn-secondary mt-1.5 !px-2.5 !py-1 text-xs" disabled={!contactName.trim() || busy} onClick={addContact}>
                  + Add contact
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function OutbreaksTab() {
  const { persona } = useAuth();
  const isEpidemiologist = persona?.roles.includes('EPIDEMIOLOGIST') ?? false;
  const canView = (isEpidemiologist || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  const listQ = useApiQuery<Page<Outbreak>>(canView ? `/api/v1/m25/outbreaks?${qs.toString()}` : null);

  return (
    <div>
      {!canView ? (
        <div className="mb-4">
          <ErrorNote message="Viewing outbreaks requires EPIDEMIOLOGIST or SENIOR_PHI." />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {OUTBREAK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {isEpidemiologist ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Declare outbreak
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No outbreaks match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">Disease</th>
                <th className="table-head">Districts</th>
                <th className="table-head">Status</th>
                <th className="table-head">Declared</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(o.id)}>
                  <td className="table-cell font-mono text-xs">{o.refNo}</td>
                  <td className="table-cell font-medium">{o.diseaseCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{o.districts.join(', ')}</td>
                  <td className="table-cell">
                    <OutbreakStatusBadge status={o.status} />
                  </td>
                  <td className="table-cell text-xs">{formatDateTime(o.declaredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateOutbreakModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <OutbreakDetailModal id={detailId} isEpidemiologist={isEpidemiologist} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function CreateOutbreakModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [diseaseCode, setDiseaseCode] = useState('');
  const [districtsInput, setDistrictsInput] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/outbreaks', {
        diseaseCode,
        districts: districtsInput.split(',').map((s) => s.trim()).filter(Boolean),
        description: description || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to declare outbreak');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Declare an outbreak" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Disease code">
          <input className="field-input" value={diseaseCode} onChange={(e) => setDiseaseCode(e.target.value.toUpperCase())} placeholder="e.g. DENGUE" />
        </Field>
        <Field label="Districts (comma separated)">
          <input className="field-input" value={districtsInput} onChange={(e) => setDistrictsInput(e.target.value)} placeholder="e.g. Victoria, Beau Vallon" />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || diseaseCode.trim().length < 2 || !districtsInput.trim()}>
            {busy ? 'Declaring…' : 'Declare outbreak'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function OutbreakDetailModal({ id, isEpidemiologist, onClose, onChanged }: { id: string; isEpidemiologist: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<Outbreak>(`/api/v1/m25/outbreaks/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const o = detailQ.data;

  const run = async (path: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/outbreaks/${id}${path}`);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Outbreak" onClose={onClose} wide>
      {detailQ.loading || !o ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs">{o.refNo}</span>
            <OutbreakStatusBadge status={o.status} />
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Disease</dt>
              <dd className="font-medium">{o.diseaseCode.replaceAll('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Districts</dt>
              <dd className="font-medium">{o.districts.join(', ')}</dd>
            </div>
            {o.description ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Notes</dt>
                <dd className="text-right font-medium">{o.description}</dd>
              </div>
            ) : null}
          </dl>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Linked cases ({o.notifications?.length ?? 0})</div>
            {(o.notifications?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No cases linked yet.</p>
            ) : (
              <ul className="space-y-1">
                {(o.notifications ?? []).map((n) => (
                  <li key={n.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs">
                    <span>{n.nin}</span>
                    <StatusBadge status={n.state} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isEpidemiologist ? (
            <div className="flex gap-2 border-t border-slate-100 pt-3">
              {o.status === 'ACTIVE' ? (
                <button className="btn-secondary" onClick={() => run('/contain')} disabled={busy}>
                  Mark contained
                </button>
              ) : null}
              {o.status !== 'CLOSED' ? (
                <button className="btn-danger" onClick={() => run('/close')} disabled={busy}>
                  Close outbreak
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
